import { useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import {
  useIsTelegramAdmin,
  useIsTelegramStaff,
  useTelegramBackendAuth,
  useTelegramWebApp,
} from "./hooks/useTelegram";
import { Admin } from "./pages/Admin";
import { Bonuses } from "./pages/Bonuses";
import { Events } from "./pages/Events";
import { AppChromeStack } from "./components/AppChromeStack";
import { Developers } from "./pages/Developers";
import { Game } from "./pages/Game";
import { GuestNotes } from "./pages/GuestNotes";
import { Home } from "./pages/Home";
import { Menu } from "./pages/Menu";
import { Market } from "./pages/Market";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Promotions } from "./pages/Promotions";
import "./App.css";

function App() {
  useTelegramWebApp();
  useTelegramBackendAuth();
  const isAdmin = useIsTelegramAdmin();
  const isStaff = useIsTelegramStaff();
  const location = useLocation();
  const navigate = useNavigate();

  const pathname = location.pathname || "/";
  const privacyBackPath = useMemo(
    () => location.state?.privacyFrom || "/",
    [location.state]
  );
  const isAdminPath = pathname === "/admin";
  const isGamePath = pathname === "/game";
  const showAdminPanel = isStaff && isAdminPath;
  const showGame = isStaff && isGamePath;
  const showAppChrome = !(isStaff && (isAdminPath || isGamePath));

  const go = (to, options) => navigate(to, options);

  if (!isStaff && (isAdminPath || isGamePath)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="appShell">
      <div className="appShell__bg" aria-hidden />
      <div className="appShell__frame">
        <div
          className={`appShell__scroll ${showAppChrome ? "appShell__scroll--chrome" : "appShell__scroll--noChrome"}`}
        >
          {showAppChrome ? (
            <AppChromeStack
              onOpenBonuses={() => go("/bonuses")}
              balanceSyncKey={pathname}
            />
          ) : null}
          {isStaff ? (
            <nav className="topNav">
              <button
                type="button"
                className={`topNav__item ${pathname === "/" ? "isActive" : ""}`}
                onClick={() => go("/")}
              >
                Миниапп
              </button>
              <button
                type="button"
                className={`topNav__item ${pathname === "/admin" ? "isActive" : ""}`}
                onClick={() => go("/admin")}
              >
                Админ
              </button>
            </nav>
          ) : null}

          <main>
            {showAdminPanel ? (
              <Admin onOpenGame={() => go("/game")} canManageHostess={isAdmin} />
            ) : null}
            {!showAdminPanel && pathname === "/events" ? (
              <Events onBack={() => go("/")} />
            ) : null}
            {!showAdminPanel && pathname === "/market" ? (
              <Market onBack={() => go("/")} />
            ) : null}
            {!showAdminPanel && pathname === "/bonuses" ? (
              <Bonuses onBack={() => go("/")} />
            ) : null}
            {!showAdminPanel && pathname === "/menu" ? (
              <Menu onBack={() => go("/")} />
            ) : null}
            {!showAdminPanel && pathname === "/guest-notes" ? (
              <GuestNotes onBack={() => go("/")} />
            ) : null}
            {!showAdminPanel && pathname === "/promotions" ? (
              <Promotions
                onBack={() => go("/")}
                onOpenPrivacyPolicy={() =>
                  go("/privacy-policy", { state: { privacyFrom: pathname } })
                }
              />
            ) : null}
            {!showAdminPanel && pathname === "/privacy-policy" ? (
              <PrivacyPolicy onBack={() => go(privacyBackPath)} />
            ) : null}
            {!showAdminPanel && pathname === "/developers" ? (
              <Developers onBack={() => go("/")} />
            ) : null}
            {!showAdminPanel && showGame ? (
              <Game onBack={() => go("/admin")} />
            ) : null}
            {!showAdminPanel &&
            !showGame &&
            pathname === "/" ? (
              <Home
                onOpenEvents={() => go("/events")}
                onOpenMarket={() => go("/market")}
                onOpenBonuses={() => go("/bonuses")}
                onOpenMenu={() => go("/menu")}
                onOpenGuestNotes={() => go("/guest-notes")}
                onOpenPromotions={() => go("/promotions")}
              />
            ) : null}
            {!showAdminPanel &&
            !showGame &&
            pathname !== "/" &&
            pathname !== "/events" &&
            pathname !== "/market" &&
            pathname !== "/bonuses" &&
            pathname !== "/menu" &&
            pathname !== "/guest-notes" &&
            pathname !== "/promotions" &&
            pathname !== "/privacy-policy" &&
            pathname !== "/developers" ? (
              <Navigate to="/" replace />
            ) : null}
          </main>

          <footer className="footerBrand" aria-label="Разработчик">
            <button
              type="button"
              className="footerBrand__line footerBrand__button"
              onClick={() => go("/developers")}
            >
              Powered by <span className="footerBrand__name">ProDG</span>
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default App;
